import { addDoc, arrayRemove, arrayUnion, doc, getDoc, getDocs, query, updateDoc, where } from 'firebase/firestore';
import {
    BranchOffice,
    branchOfficesRef,
    keepProtectedBranchFields,
    withNormalizedManagerPhone
} from '@api/BranchOffices/BranchOffice';
import { getCurrentPermissionSubject } from '@utils/Permissions';
import { can, Capability } from '@utils/Permissions/Capabilities';
import ActivityLogServ from '@api/ActivityLogs/ActivityLogServ';
import { describeChanges } from '@api/ActivityLogs/DescribeChanges';
import { BRANCH_OFFICE_FIELDS, DELIVERY_FIELDS } from '@api/ActivityLogs/ConfigActivity';
import { usersRef, UserType } from '@api/Users/User';

export default {
    /**
     * Guarda la sucursal **dejando dicho qué cambió** (M5 de la bitácora,
     * 2026-09-11). Aquí viven las tarifas de envío y los horarios: lo que se le
     * cobra al cliente y si la sucursal aparece abierta. Hasta hoy se podían
     * mover sin rastro, y **desde DOS pantallas distintas** (el atajo del sidebar
     * y el formulario de Sucursales), así que ni siquiera se sabía por cuál.
     *
     * El detalle del envío se describe aparte (`DELIVERY_FIELDS`) porque va
     * anidado: sin eso el renglón diría "cambió las tarifas" sin decir a cuánto.
     *
     * Costo: 1 lectura del estado anterior + 1 escritura, **solo si cambió algo**.
     * Lo más frecuente que pasa por aquí es abrir/cerrar delivery — unas cuantas
     * veces al día entre las diez sucursales.
     */
    async saveBranchOffice(branchOffice: BranchOffice): Promise<BranchOffice> {
        // El teléfono del responsable, a 10 dígitos SIEMPRE: de aquí sale el
        // WhatsApp de "pedido nuevo" y un `+52` capturado a mano lo cancelaba.
        branchOffice = withNormalizedManagerPhone(branchOffice);
        // ⚠️ La lectura del estado ANTERIOR es de la bitácora, no del guardado:
        // va en try/catch para que un fallo de red **no impida guardar la
        // sucursal**. Sin esto, la auditoría se vuelve la causa de que algo no
        // pase — que es exactamente lo que el plan viene a evitar.
        let anterior: BranchOffice | undefined;
        try {
            anterior = branchOffice.id ? await this.getBranchOffice(branchOffice.id) : undefined;
        } catch (error) {
            console.error('No se pudo leer la sucursal anterior para la bitácora', error);
        }
        // Gate (plan de Gastos F6): dar de alta una sucursal y cambiar su número, su CEDIS
        // o su inventario es del admin general. El admin de sucursal edita lo demás de
        // las suyas; el gerente, solo los servicios desde el atajo del sidebar.
        const subject = getCurrentPermissionSubject();
        const protectedOk = can(subject, Capability.BranchEditProtected);
        if (!branchOffice.id && !protectedOk) throw new Error('Dar de alta una sucursal es del admin general');
        if (branchOffice.id) {
            const scope = { branchOfficeId: branchOffice.id };
            if (!can(subject, Capability.BranchEdit, scope) && !can(subject, Capability.BranchServicesToggle, scope)) {
                throw new Error('Esta sucursal no es tuya');
            }
            if (!protectedOk) {
                if (!anterior) throw new Error('No se pudo leer la sucursal: vuelve a intentar');
                branchOffice = keepProtectedBranchFields(branchOffice, anterior);
            }
        }
        if (!branchOffice.id) {
            const data = await addDoc(branchOfficesRef, branchOffice);
            branchOffice.id = data.id;
        }
        await updateDoc(doc(branchOfficesRef, branchOffice.id), { ...branchOffice });
        try {
            if (anterior) {
                const frase = [
                    describeChanges(anterior, branchOffice, BRANCH_OFFICE_FIELDS),
                    describeChanges(anterior.delivery || {}, branchOffice.delivery || {}, DELIVERY_FIELDS)
                ]
                    .filter((texto) => texto !== '')
                    .join(' y ');
                if (frase) {
                    await ActivityLogServ.addActivityLog('branchOffice', branchOffice.id, frase);
                }
            }
        } catch (error) {
            console.error('No se pudo registrar el cambio de la sucursal', error);
        }
        return branchOffice;
    },
    async getBranchOffices(filters?: any): Promise<BranchOffice[]> {
        const branchOffices: BranchOffice[] = [];
        const filtersQuery = [];
        if (filters && filters.distributionCenter !== 'all') {
            filtersQuery.push(where('distributionCenterId', '==', filters.distributionCenter));
        }
        const snapshot = await getDocs(query(branchOfficesRef, ...filtersQuery));
        if (snapshot.empty) {
            return branchOffices;
        }
        snapshot.docs.map((doc: any) => {
            branchOffices.push(doc.data() as BranchOffice);
        });
        return branchOffices;
    },
    async getBranchOffice(branchOfficeId: string): Promise<BranchOffice> {
        const documentData = await getDoc(doc(branchOfficesRef, branchOfficeId));
        return documentData.data() as BranchOffice;
    },
    // "86" de insumo por sucursal: arrayUnion/arrayRemove son atómicos por elemento,
    // así dos dispositivos marcando a la vez no se pisan (sin transacciones, como todo el sistema)
    async setInputUnavailability(
        branchOfficeId: string,
        inputId: string,
        inputName: string,
        unavailable: boolean
    ): Promise<void> {
        await updateDoc(doc(branchOfficesRef, branchOfficeId), {
            unavailableInputs: unavailable ? arrayUnion(inputId) : arrayRemove(inputId)
        });
        await ActivityLogServ.addActivityLog(
            'branchOffice',
            branchOfficeId,
            unavailable ? `marcó agotado el insumo "${inputName}"` : `reabrió el insumo "${inputName}"`
        );
        return;
    },
    async toggleStatus(branchOfficeId: string): Promise<void> {
        if (!can(getCurrentPermissionSubject(), Capability.BranchEdit, { branchOfficeId })) {
            throw new Error('Esta sucursal no es tuya');
        }
        const branchOffice = await this.getBranchOffice(branchOfficeId);
        const status = !branchOffice.status;
        await updateDoc(doc(branchOfficesRef, branchOfficeId), { status });
        try {
            await ActivityLogServ.addActivityLog(
                'branchOffice',
                branchOfficeId,
                status ? 'activó la sucursal' : 'desactivó la sucursal'
            );
        } catch (error) {
            console.error('No se pudo registrar el cambio de estado de la sucursal', error);
        }
        return;
    },
    async getTeam(branchOfficeId: string): Promise<UserType[]> {
        const users: UserType[] = [];
        const queryUsersDoc = query(
            usersRef,
            where('profile.branchOffices', 'array-contains-any', [branchOfficeId, 'all'])
        );
        const snapshot = await getDocs(queryUsersDoc);
        if (snapshot.empty) {
            return users;
        }
        snapshot.docs.map((doc: any) => {
            users.push(doc.data() as UserType);
        });
        return users;
    }
};
